import React from 'react';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import Download from 'apollo-react-icons/Download';
import Eye from 'apollo-react-icons/EyeShow';
import FileText from 'apollo-react-icons/File';
import { toast } from 'react-toastify';
import useFileDownload from '../../hooks/api/files/useFileDownload';
import {
  getDisplayMimeLabel,
  isFilePreviewableByDescriptor,
} from '../../utils/filePreviewSupport';
import DownloadProgress from './DownloadProgress';

/**
 * View-only file display for HCP license registry / request detail.
 * Uses the shared authenticated file module download/preview helpers.
 */
const ViewOnlyFileDisplay = ({ files = [], label }) => {
  const { downloadFile, downloadFileForEndUserFilledForm, downloading } =
    useFileDownload();

  const handleDownload = async (fileId, fileName) => {
    await downloadFile(fileId, fileName);
  };

  const handlePreview = async (file) => {
    const fileId = file.id || file.file_id;
    const fileName =
      file.name || file.file_name || file.original_name || `file-${fileId}`;
    try {
      await downloadFileForEndUserFilledForm(
        {
          id: fileId,
          name: fileName,
          mime_type: file.mime_type || file.type,
        },
        { forceDownload: false }
      );
    } catch (error) {
      console.error('Error opening preview:', error);
      toast.error(`Failed to open preview for ${fileName}`, {
        position: 'top-center',
      });
    }
  };

  const filesArray = Array.isArray(files) ? files : [files].filter(Boolean);

  return (
    <Box sx={{ marginBottom: 2 }}>
      {label ? (
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            marginBottom: 1,
            color: '#333',
          }}
        >
          {label}
        </Typography>
      ) : null}

      {filesArray.length > 0 ? (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            padding: 2,
            backgroundColor: '#f8f9fa',
          }}
        >
          {filesArray.map((file, index) => {
            const fileId = file.id || file.file_id;
            const fileName =
              file.name ||
              file.file_name ||
              file.original_name ||
              `File ${index + 1}`;
            const displayMime = getDisplayMimeLabel(file);
            const isPreviewable = isFilePreviewableByDescriptor(file);

            return (
              <Box key={fileId != null ? String(fileId) : `f-${index}`}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: 1,
                    marginBottom: 0,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #ddd',
                  }}
                >
                  <FileText size={16} color="#666" />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {fileName}
                    {displayMime && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ display: 'block', color: '#888' }}
                      >
                        {displayMime}
                      </Typography>
                    )}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isPreviewable && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Eye size={14} />}
                        onClick={() => handlePreview(file)}
                        sx={{
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                            borderColor: '#1976d2',
                          },
                        }}
                      >
                        Preview
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download size={14} />}
                      onClick={() => handleDownload(fileId, fileName)}
                      disabled={downloading}
                      sx={{ minWidth: 'auto' }}
                    >
                      {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Box>
                </Box>

                <DownloadProgress
                  fileId={`download_${fileId}`}
                  fileName={fileName}
                />

                {index < filesArray.length - 1 && (
                  <Box sx={{ marginBottom: 1 }} />
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            padding: 2,
            backgroundColor: '#f8f9fa',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No file uploaded
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ViewOnlyFileDisplay;
